import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { AnonymousLayout } from "@/layouts/AnonymousLayout/AnonymousLayout";
import { ChangePasswordForm } from "@/modules/form/components/ChangePasswordForm/ChangePasswordForm";
import { anonymousRouting } from "@/modules/router/routings/anonymousRouting";
import { AnonymousPageBox } from "@/modules/theme/components/AnonymousPageBox";
import { PageMeta } from "@/modules/theme/components/PageMeta";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";

import { createSetupPasswordStyles } from "./createSetupPasswordStyles";
import { useSetPasswordMutation } from "./useSetupPasswordMutation";

const translationNamespace = "pages/SetupPasswordPage";

export function SetupPasswordPage() {
  const history = useHistory();
  const classes = createSetupPasswordStyles(useTheme());
  const query = anonymousRouting.useRouteQuery("setupPassword");
  const { t } = useTranslation(translationNamespace);
  const setPasswordMutation = useSetPasswordMutation();
  const token = query?.token || "";

  const onValidSubmit = useCallback(
    ({ newPassword }: { newPassword: string }) => {
      setPasswordMutation.mutate({
        password: newPassword,
        token,
      });
    },
    [token, setPasswordMutation]
  );

  useEffect(() => {
    if (!token) {
      history.replace(anonymousRouting.buildUrl("signIn"));
    }
  }, [history, token]);

  return (
    <AnonymousLayout>
      <AnonymousPageBox>
        <PageMeta title={t("Set up a password for your account")} />
        <div className={classes.textWrapper}>
          <Typography variant="heading" className={classes.heading}>
            {t("Set up a password for your account")}
          </Typography>
          <Typography
            variant="contentSmall"
            className={classes.passwordValidationInfo}
          >
            {t(
              "The password should be at least 6 characters long, have min. 1 uppercase letter, 1 digit and 1 special character."
            )}
          </Typography>
        </div>
        <ChangePasswordForm
          isLoading={setPasswordMutation.status === "loading"}
          onValidSubmit={onValidSubmit}
          mode="setup"
        />
      </AnonymousPageBox>
    </AnonymousLayout>
  );
}
